import { Component, Input } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { Router } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MetricResult, MetricEvidence } from '../../interfaces/metrics';
import { WIDGET_CATALOG } from '../widget-catalog';

const MAX_TABLE_ROWS = 15;

interface FlatEntry { key: string; label: string; value: any; }
interface DrillView { columns: string[]; rows: any[]; more: number; flat: FlatEntry[]; }

@Component({
  selector: 'bst-drilldown-panel',
  templateUrl: './drilldown-panel.component.html',
  styleUrls: ['./drilldown-panel.component.scss'],
  standalone: false,
  // DatePipe/DecimalPipe are not root-provided; without this the dialog
  // component fails DI at runtime and the overlay renders empty/unclosable.
  providers: [DatePipe, DecimalPipe]
})
export class DrilldownPanelComponent {
  @Input() result!: MetricResult;
  // Optional second result the caller already holds, walked after `result` as extra
  // evidence for the same card (e.g. hours.byday, fetched beside hours.week). Kept
  // optional and caller-supplied: this panel never fetches, so a drawer can only ever
  // show what the dashboard already loaded.
  @Input() extra?: MetricResult;

  // The basis line says WHICH HOURS a number counts, driven by the resolver's OWN
  // evidence (Phase 2) rather than a hardcoded key allow-list: a resolver that counts
  // no hours declares { notApplicable: true } and this line renders nothing, so a new
  // metric defaults to silence instead of to a wrong sentence (same rule as the old
  // allow-list, now self-describing instead of duplicated here).
  get evidence(): MetricEvidence | null {
    const e = (this.result as any).evidence as MetricEvidence | undefined;
    return e && !e.notApplicable ? e : null;
  }

  // Derived from the ACTUAL approval states the period counted (open periods count
  // drafts too - see BaseResolver::hoursEvidence), not a static approved/mixed guess.
  get basisLabelKey(): string | null {
    const states = this.evidence?.approvalStatesIncluded;
    if (!states || !states.length) { return null; }
    if (states.includes('draft')) { return 'dashboard.drill.basis_open'; }
    if (states.length === 1 && states[0] === 'approved') { return 'dashboard.drill.basis_approved'; }
    return 'dashboard.drill.basis_mixed';
  }

  // Built once per drawer open (result arrives via dialog context and never
  // changes after); keeps the getters below cheap across change detection.
  private _view?: DrillView;

  constructor(private ref: NbDialogRef<DrilldownPanelComponent>,
              private router: Router,
              private translate: TranslateService,
              private datePipe: DatePipe,
              private decimalPipe: DecimalPipe) {}

  get titleKey(): string | undefined {
    return WIDGET_CATALOG.find(w => w.metric === this.result.key)?.titleKey;
  }

  // Mirrors the card's mandatory derived-money caption ("billable value, not
  // invoiced") for metrics whose catalog entry declares it. Without this, the
  // margin drawers showed absolute € amounts with no hint they are modeled
  // from rates rather than invoiced.
  get derivedMoney(): boolean {
    return !!WIDGET_CATALOG.find(w => w.metric === this.result.key)?.derivedMoney;
  }

  private get view(): DrillView { return this._view ??= this.buildView(); }

  // Column is numeric if ANY row holds a number there (first row may be null).
  isNumCol(col: string): boolean { return this.tableRows.some(r => typeof r?.[col] === 'number'); }
  get columns(): string[] { return this.view.columns; }
  get tableRows(): any[] { return this.view.rows; }
  get moreCount(): number { return this.view.more; }
  get flatEntries(): FlatEntry[] { return this.view.flat; }

  // Walks the payload once. The FIRST top-level array-of-objects field becomes
  // the table (`rows`, `days`, `cells`...), capped at MAX_TABLE_ROWS; other
  // arrays (e.g. sparkline: number[]) are skipped. Primitive fields become
  // label/value rows; nested object fields (totals/current/previous) are
  // flattened one level with 'Parent · Child' labels. Table and flat rows may
  // coexist (e.g. hours.byday: days table + totals rows).
  // `data` only exists on the 'ok' variant of the MetricResult union — the only
  // state that reaches this panel (WidgetHostComponent only emits drilldown for
  // WidgetView kind 'data').
  private buildView(): DrillView {
    const v: DrillView = { columns: [], rows: [], more: 0, flat: [] };
    this.walk((this.result as any)?.data ?? {}, v);
    if (this.extra) {
      // The primary payload wins every collision: the extra result is evidence FOR the
      // card, so re-listing its own headline numbers (hours.byday's totals repeat
      // hours.week's registered/planned) would only pad the drawer. Skipping is scoped
      // to keys the primary already emitted, never within one payload, where repeated
      // keys are distinct facts (current.status vs previous.status).
      this.walk((this.extra as any)?.data ?? {}, v, new Set(v.flat.map(e => e.key)));
    }
    return v;
  }

  private walk(p: any, v: DrillView, skipKeys?: Set<string>): void {
    for (const key of Object.keys(p)) {
      const val = p[key];
      if (Array.isArray(val)) {
        if (!v.rows.length && val.length && val[0] !== null && typeof val[0] === 'object' && !Array.isArray(val[0])) {
          // Union of primitive keys across ALL rows: optional fields present
          // only in later rows must still get a column.
          const cols: string[] = [];
          for (const row of val) {
            for (const c of Object.keys(row ?? {})) {
              if (c === 'userId') { continue; }
              if (!cols.includes(c) && val.some((r: any) => this.isPrimitive(r?.[c]) && r?.[c] !== null)) { cols.push(c); }
            }
          }
          v.columns = cols;
          v.rows = val.slice(0, MAX_TABLE_ROWS);
          v.more = val.length - v.rows.length;
        }
      } else if (this.isPrimitive(val)) {
        if (!skipKeys?.has(key)) {
          v.flat.push({ key, label: this.label(key), value: val });
        }
      } else {
        for (const child of Object.keys(val)) {
          if (this.isPrimitive(val[child]) && !skipKeys?.has(child)) {
            v.flat.push({ key: child, label: this.label(key) + ' · ' + this.label(child), value: val[child] });
          }
        }
      }
    }
  }

  isNum(v: any): boolean {
    return typeof v === 'number';
  }

  private isPrimitive(v: any): boolean {
    return v === null || typeof v !== 'object';
  }

  // Translate 'dashboard.field.<key>'; ngx-translate returns the key itself when
  // there's no translation, so fall back to a humanized version of the key.
  label(key: string): string {
    const path = 'dashboard.field.' + key;
    const translated = this.translate.instant(path);
    return translated === path ? this.humanize(key) : translated;
  }

  private humanize(key: string): string {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  // Enum-valued payload fields, mapped to the SAME translation keys the cards
  // use, so a value never renders differently in the drawer than on the tile.
  // 'status' tries the timesheet namespace first (self drawers), then the
  // company roster one ('bench'/'rolloff'); a miss in both falls through raw.
  private static readonly ENUM_NAMESPACES: { [key: string]: string[] } = {
    status: ['dashboard.ts_status.', 'dashboard.company.status_'],
    timesheetStatus: ['dashboard.ts_status.'],
    signal: ['dashboard.company.signal_'],
    severity: ['dashboard.company.sev_'],
    kind: ['dashboard.company.kind_'],
  };

  formatValue(key: string, v: any): string {
    if (v === null || v === undefined) { return '—'; }
    if (typeof v === 'boolean') {
      return this.translate.instant(v ? 'dashboard.field.yes' : 'dashboard.field.no');
    }
    const namespaces = DrilldownPanelComponent.ENUM_NAMESPACES[key];
    if (namespaces && typeof v === 'string') {
      for (const ns of namespaces) {
        const translated = this.translate.instant(ns + v);
        if (translated !== ns + v) { return translated; }
      }
      return String(v);
    }
    // camelCase xxxOn/xxxDate only; no /i on the "On" arm or duration/reason/
    // position (numeric fields ending in lowercase "on") render as epoch dates.
    const isDateField = /[a-z](Date|On)$/.test(key) || /^date$/i.test(key)
      || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(v));
    if (isDateField) {
      return this.datePipe.transform(v, 'd MMM y') || String(v);
    }
    if (typeof v === 'number') {
      const k = key.toLowerCase();
      // 'hrs' covers the margin resolvers' billableHrs/coveredHrs pair; without it
      // two adjacent rows in the same unit rendered one labelled, one bare.
      if (k.includes('hours') || k.includes('hrs') || k.includes('registered') || k.includes('planned') || k.includes('billable')) {
        return v + ' ' + this.translate.instant('dashboard.unit.hours_suffix');
      }
      if (key === 'amount' || key === 'revenue' || key === 'cost') {
        return '€ ' + this.decimalPipe.transform(v, '1.0-0');
      }
      // The *Pct suffix is the company resolvers' percent convention
      // (marginPct, coveragePct, sharePct...); the named keys are the
      // self/team and summary ones that predate it.
      if (/pct$/i.test(key) || key === 'value' || key === 'target' || key === 'utilization'
          || key === 'companyUtilization' || key === 'currentUtil') {
        return v + '%';
      }
    }
    return String(v);
  }

  close(): void {
    this.ref.close();
  }

  // Evidence rows must lead somewhere real: per-key source screens, one entry
  // per metric key that has an unambiguous destination. A key with no entry
  // here gets NO link (see hasSource/openSource below) rather than a guessed
  // one, since a wrong destination is worse than no destination.
  //
  // '/users' and '/projects' do NOT resolve to the table: 'users'/'projects' redirect
  // (pathMatch: full) to 'users/view/'|'projects/view/', but the earlier 'users/:id'/
  // 'projects/:id' routes swallow 'view' as an id before the later ':table/view/:type'
  // route is ever tried, and that route requires a non-empty :type besides. Route to
  // the concrete working path instead (Type.Active = 'active', the table's own default
  // first tab; see app-routing.module.ts and table-object.component.ts).
  private static readonly SOURCE_ROUTES: { [key: string]: string } = {
    'team.to_approve': '/time-approvals',
    'timesheet.status': '/registration/hours/calendar',
    'team.rolloff': '/users/view/active',
    'team.capacity_forecast': '/users/view/active',
    'team.roster': '/users/view/active',
    'hours.week': '/registration/hours/calendar',
    'hours.byday': '/registration/hours/calendar',
    'hours.heatmap': '/registration/hours/calendar',
    'utilization.self': '/registration/hours/calendar',
    'contribution.self': '/registration/hours/calendar',
    'assignments.ending': '/projects/view/active',
    // leave.self deliberately has no route: its only render surface is the
    // member-drill (a report, not the actor), where the old '/users/me' target
    // would open the viewing manager's own profile.
    // Company drawers about people and headcount lead to the member table. The
    // rest deliberately get no route: the financial ones (margin,
    // margin_by_client) and summary/util_timeseries have no screen showing
    // those numbers closer to source, and bench_forecast's weekly buckets are
    // a forward projection no list screen shows at all.
    'company.attention': '/users/view/active',
    'company.coming_free': '/users/view/active',
    'company.bench': '/users/view/active',
    'company.bench_roster': '/users/view/active',
  };

  // The button used to say "Open in Hours" for every destination, including the ones that
  // go to the approvals queue, the member list and the project list. Labelled by where it
  // actually goes, keyed by route so two metrics landing on the same screen can never
  // disagree about what that screen is called.
  private static readonly SOURCE_LABELS: { [route: string]: string } = {
    '/registration/hours/calendar': 'dashboard.drill.open_source',
    '/time-approvals': 'dashboard.drill.open_approvals',
    '/users/view/active': 'dashboard.drill.open_members',
    '/projects/view/active': 'dashboard.drill.open_projects',
  };

  get hasSource(): boolean {
    return !!DrilldownPanelComponent.SOURCE_ROUTES[this.result.key];
  }

  get sourceLabelKey(): string {
    const target = DrilldownPanelComponent.SOURCE_ROUTES[this.result.key];
    return DrilldownPanelComponent.SOURCE_LABELS[target] ?? 'dashboard.drill.open_source';
  }

  openSource(): void {
    const target = DrilldownPanelComponent.SOURCE_ROUTES[this.result.key];
    if (!target) { return; }
    this.ref.close();
    this.router.navigate([target]).catch(err => console.error('dashboard: navigation failed', err));
  }

  openUser(userId: number): void {
    this.ref.close();
    this.router.navigate(['/users', userId]).catch(err => console.error('dashboard: navigation failed', err));
  }
}
